import React, { useState, useEffect } from 'react';
import ReportTable, { Column } from '../ReportTable';
import { Property, Invoice, Transaction, SalaryPayment, MaintenanceRequest } from '../../types';
import { api } from '../../services/api';

interface FinancialReportsProps {
  properties: Property[];
  invoices: Invoice[];
  transactions: (Transaction & { type?: 'Expense' | 'Income' })[];
  salaries?: SalaryPayment[];
  maintenance?: MaintenanceRequest[];
  initialReport?: string;
}

const FinancialReports: React.FC<FinancialReportsProps> = ({ properties, invoices, transactions, salaries = [], maintenance = [], initialReport }) => {
  const [selectedReport, setSelectedReport] = useState<string>(initialReport || 'monthly');
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('All');
  const [accountingPlData, setAccountingPlData] = useState<any>(null);
  const [txTypeFilter, setTxTypeFilter] = useState<'all' | 'income' | 'expense'>('all');

  useEffect(() => {
    if (selectedReport === 'pnl') {
      api.accounting.getPlReport(selectedYear, selectedPropertyId)
        .then(data => setAccountingPlData(data))
        .catch(err => console.error("Error fetching P&L report:", err));
    }
  }, [selectedReport, selectedYear, selectedPropertyId]);

  useEffect(() => {
    if (initialReport) {
      setSelectedReport(initialReport);
    }
  }, [initialReport]);

  const years = Array.from(new Set([...invoices, ...transactions].map(i => new Date(i.date).getFullYear()))).sort((a,b) => b-a);

  // 1. تقرير العوائد الشهرية
  const months = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
  const monthlyData = months.map((m, idx) => {
      const monthlyInvs = invoices.filter(inv => {
          if (inv.status !== 'Paid' || !inv.paymentDate) return false;
          const d = new Date(inv.paymentDate);
          return d.getFullYear().toString() === selectedYear && d.getMonth() === idx;
      });
      const revenue = monthlyInvs.reduce((sum, i) => sum + Number(i.amount) + Number(i.serviceCharge || 0), 0);
      return { month: m, revenue };
  });
  const monthlyColumns: Column[] = [
      { header: 'الشهر', accessor: 'month' },
      { header: 'إجمالي المحصل', accessor: 'revenue', render: val => <span className="font-bold text-emerald-600">{Number(val).toLocaleString()} د.ك</span> }
  ];

  // 2. تحليل "عمر الديون" (Aging Debts)
  const today = new Date();
  const agingData = invoices.filter(i => i.status === 'Pending').map(i => {
      const due = new Date(i.dueDate || i.date);
      const diffTime = today.getTime() - due.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      let agingCategory = '0-30 يوم';
      if (diffDays > 90) agingCategory = '90+ يوم (حرج)';
      else if (diffDays > 60) agingCategory = '61-90 يوم';
      else if (diffDays > 30) agingCategory = '31-60 يوم';

      return {
          tenant: i.tenantName,
          unit: `${i.propertyName} - ${i.unitNumber}`,
          dueDate: due.toLocaleDateString('ar-KW'),
          amount: i.amount,
          days: diffDays,
          category: agingCategory
      };
  }).sort((a,b) => b.days - a.days);

  const agingColumns: Column[] = [
      { header: 'المستأجر', accessor: 'tenant' },
      { header: 'العقار والوحدة', accessor: 'unit' },
      { header: 'تاريخ الاستحقاق', accessor: 'dueDate' },
      { header: 'المبلغ المستحق', accessor: 'amount', render: val => `${Number(val).toLocaleString()} د.ك` },
      { header: 'فئة التأخير', accessor: 'category', render: val => (
          <span className={`px-3 py-1 rounded-full text-[10px] font-black ${
              val.includes('حرج') ? 'bg-rose-100 text-rose-700' : val.includes('31') ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'
          }`}>{val}</span>
      )}
  ];

  // 3. الربح والخسارة (P&L) (الآن يقرأ من القيود المحاسبية الدقيقة ويقسم الإيرادات إلى محصلة وتحت التحصيل)
  let pnlData: any[] = [];
  if (accountingPlData) {
      const filteredInvs = invoices.filter(inv => {
          const invYear = (inv.monthDue || '').substring(0, 4);
          const matchesYear = invYear === selectedYear;
          const matchesProperty = selectedPropertyId === 'All' || String(inv.propertyId) === String(selectedPropertyId);
          return matchesYear && matchesProperty;
      });

      const collectedRent = filteredInvs.filter(i => i.status === 'Paid').reduce((sum, i) => sum + Number(i.amount), 0);
      const uncollectedRent = filteredInvs.filter(i => i.status !== 'Paid').reduce((sum, i) => sum + Number(i.amount), 0);

      const collectedSvc = filteredInvs.filter(i => i.status === 'Paid').reduce((sum, i) => sum + Number(i.serviceCharge || 0), 0);
      const uncollectedSvc = filteredInvs.filter(i => i.status !== 'Paid').reduce((sum, i) => sum + Number(i.serviceCharge || 0), 0);

      const revenuesList: any[] = [];
      const expensesList: any[] = [];
      
      let hasRentFromJournal = false;
      let hasSvcFromJournal = false;

      accountingPlData.details.forEach((d: any) => {
          if (d.accountName === 'إيرادات تأجير العقارات') {
              hasRentFromJournal = true;
              const standaloneRent = Number(d.balance) - (collectedRent + uncollectedRent);
              revenuesList.push({ category: 'إيرادات تأجير العقارات (فواتير محصلة)', amount: collectedRent, type: 'revenue' });
              if (uncollectedRent > 0) {
                  revenuesList.push({ category: 'إيرادات تأجير العقارات (فواتير تحت التحصيل)', amount: uncollectedRent, type: 'revenue_uncollected' });
              }
              if (standaloneRent > 0) {
                  revenuesList.push({ category: 'إيرادات إيجارات أخرى (حرة/إضافية)', amount: standaloneRent, type: 'revenue' });
              }
          } else if (d.accountName === 'إيرادات رسوم الخدمات') {
              hasSvcFromJournal = true;
              const standaloneSvc = Number(d.balance) - (collectedSvc + uncollectedSvc);
              revenuesList.push({ category: 'إيرادات رسوم الخدمات (فواتير محصلة)', amount: collectedSvc, type: 'revenue' });
              if (uncollectedSvc > 0) {
                  revenuesList.push({ category: 'إيرادات رسوم الخدمات (فواتير تحت التحصيل)', amount: uncollectedSvc, type: 'revenue_uncollected' });
              }
              if (standaloneSvc > 0.001) {
                  revenuesList.push({ category: 'إيرادات رسوم خدمات أخرى (حرة/إضافية)', amount: standaloneSvc, type: 'revenue' });
              }
          } else {
              if (d.type === 'Revenue') {
                  revenuesList.push({ category: d.accountName, amount: d.balance, type: 'revenue' });
              } else {
                  expensesList.push({ category: d.accountName, amount: d.balance, type: 'expense' });
              }
          }
      });

      if (!hasRentFromJournal && (collectedRent > 0 || uncollectedRent > 0)) {
          if (collectedRent > 0) revenuesList.push({ category: 'إيرادات تأجير العقارات (محصلة)', amount: collectedRent, type: 'revenue' });
          if (uncollectedRent > 0) revenuesList.push({ category: 'إيرادات تأجير العقارات (تحت التحصيل)', amount: uncollectedRent, type: 'revenue_uncollected' });
      }
      if (!hasSvcFromJournal && (collectedSvc > 0 || uncollectedSvc > 0)) {
          if (collectedSvc > 0) revenuesList.push({ category: 'إيرادات رسوم الخدمات (فواتير محصلة)', amount: collectedSvc, type: 'revenue' });
          if (uncollectedSvc > 0) revenuesList.push({ category: 'إيرادات رسوم الخدمات (فواتير تحت التحصيل)', amount: uncollectedSvc, type: 'revenue_uncollected' });
      }

      let calcTotalRevenue = 0;
      let calcTotalExpense = 0;
      revenuesList.forEach(r => calcTotalRevenue += Number(r.amount || 0));
      expensesList.forEach(e => calcTotalExpense += Number(e.amount || 0));
      let calcNetProfit = calcTotalRevenue - calcTotalExpense;

      pnlData.push({ category: '🔹 الإيـــرادات', amount: null, type: 'header' });
      pnlData = pnlData.concat(revenuesList);
      if (revenuesList.length === 0) pnlData.push({ category: 'لا توجد إيرادات مسجلة', amount: 0, type: 'empty' });
      pnlData.push({ category: '(إجمالي الإيرادات المكتسبة)', amount: calcTotalRevenue, type: 'revenue_total' });
      
      pnlData.push({ category: '🔸 المصروفـــات والتكاليـــف', amount: null, type: 'header' });
      pnlData = pnlData.concat(expensesList);
      if (expensesList.length === 0) pnlData.push({ category: 'لا توجد مصروفات مسجلة', amount: 0, type: 'empty' });
      pnlData.push({ category: '(إجمالي التكاليف والمصروفات)', amount: calcTotalExpense, type: 'expense_total' });
      
      pnlData.push({ category: '🎯 صافـــي النتيجـــة', amount: null, type: 'header' });
      pnlData.push({ category: 'صافي الربح النهائي (الاستحقاق)', amount: calcNetProfit, type: 'net' });

  } else {
      pnlData = [{ category: 'جاري تحميل البيانات المحاسبية...', amount: 0, type: 'net' }];
  }
  const pnlColumns: Column[] = [
      { header: 'البند المحاسبي', accessor: 'category', render: (val, row) => (
          <span className={`${row.type === 'header' ? 'font-black text-slate-800 text-lg bg-slate-100 px-4 py-2 rounded block w-full' : row.type === 'empty' ? 'text-slate-400 italic pl-4 block' : 'pl-4 block'}`}>
              {val}
          </span>
      )},
      { header: 'القيمة الإجمالية', accessor: 'amount', render: (val, row) => {
          if (row.type === 'header' || row.type === 'empty') return <span></span>;
          
          return <span className={`font-black inline-block min-w-[100px] ${
              row.type === 'revenue' ? 'text-emerald-600' : 
              row.type === 'revenue_uncollected' ? 'text-amber-500 italic' : 
              row.type === 'expense' ? 'text-rose-600' : 
              row.type === 'revenue_total' ? 'text-emerald-800 text-lg bg-emerald-100 px-3 py-1 rounded' :
              row.type === 'expense_total' ? 'text-rose-800 text-lg bg-rose-100 px-3 py-1 rounded' :
              Number(val) >= 0 ? 'text-fly-700 underline text-xl bg-fly-100 px-4 py-2 rounded' : 'text-rose-700 underline text-xl bg-rose-100 px-4 py-2 rounded'
          }`}>
              {val === null ? '' : `${Number(val).toLocaleString()} د.ك`}
          </span>
      }}
  ];

  // 4. عوائد الوحدات التفصيلي
  const selectedProps = selectedPropertyId === 'All' ? properties : properties.filter(p => String(p.id) === String(selectedPropertyId));
  const unitsYieldData = selectedProps.flatMap(p => 
      (p.units || []).map(u => {
          const unitInvs = invoices.filter(i => String(i.unitId) === String(u.id) && i.status === 'Paid' && new Date(i.paymentDate || i.date).getFullYear().toString() === selectedYear);
          const rev = unitInvs.reduce((sum, inv) => sum + Number(inv.amount) + Number(inv.serviceCharge || 0), 0);
          
          const propExps = transactions.filter(e => String(e.propertyId) === String(p.id) && e.type === 'Expense' && new Date(e.date).getFullYear().toString() === selectedYear);
          
          // المصروفات المباشرة للوحدة
          const directExp = propExps.filter(e => String(e.unitId) === String(u.id)).reduce((sum, e) => sum + Number(e.amount), 0);
          
          // المصروفات المشتركة للعقار (حيث لا ترتبط بوحدة معينة)
          const totalSharedPropExp = propExps.filter(e => !e.unitId).reduce((sum, e) => sum + Number(e.amount), 0);
          const sharedExp = totalSharedPropExp / (p.units?.length || 1);
          
          const totalUnitExp = directExp + sharedExp;

          return {
              propertyName: p.name,
              unitNumber: u.unitNumber,
              revenue: rev,
              expense: totalUnitExp,
              net: rev - totalUnitExp
          };
      })
  ).sort((a,b) => b.net - a.net);

  const unitsYieldColumns: Column[] = [
      { header: 'العقار', accessor: 'propertyName' },
      { header: 'رقم الوحدة', accessor: 'unitNumber' },
      { header: 'إيراد الوحدة', accessor: 'revenue', render: val => `${Number(val).toLocaleString()} د.ك` },
      { header: 'نصيبها من المصروف', accessor: 'expense', render: val => `${Number(val).toLocaleString()} د.ك` },
      { header: 'صافي الربح', accessor: 'net', render: val => <span className={`font-bold ${Number(val) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{Number(val).toLocaleString()} د.ك</span> }
  ];

  // 5. حركات الخزينة ودفتر اليومية (General Ledger)
  // Now strictly dependent on transactions, avoiding virtual duplicate aggregations
  const allLedgerData = transactions.map(e => ({
          date: new Date(e.date),
          type: e.type || 'Expense',
          category: e.categoryName || 'مصروف/إيراد',
          description: e.description || (e.type === 'Income' ? 'إيراد إضافي' : 'مصروف'),
          amount: e.amount
      })).filter(t => t.date.getFullYear().toString() === selectedYear).sort((a,b) => b.date.getTime() - a.date.getTime());

  const ledgerData = allLedgerData.map(t => ({
      ...t,
      dateString: t.date.toLocaleDateString('ar-KW')
  }));

  const ledgerColumns: Column[] = [
      { header: 'التاريخ', accessor: 'dateString' },
      { header: 'نوع الحركة', accessor: 'category' },
      { header: 'البيان والتفاصيل', accessor: 'description' },
      { header: 'إيراد (+)', accessor: 'type', render: (val, row) => val === 'Income' ? <span className="font-bold text-emerald-600">{Number(row.amount).toLocaleString()} د.ك</span> : '-' },
      { header: 'مصروف (-)', accessor: 'type', render: (val, row) => val === 'Expense' ? <span className="font-bold text-rose-600">{Number(row.amount).toLocaleString()} د.ك</span> : '-' }
  ];

  // 6. سجل الإيرادات والمصروفات التفصيلي
  const allTransactionsData = transactions.map(e => ({
          id: `tx-${e.id}`,
          date: new Date(e.date),
          propertyName: e.propertyName || 'عام',
          propertyId: e.propertyId || 'All',
          category: e.categoryName || (e.type === 'Income' ? 'إيراد عام' : 'مصروف عام'),
          amount: e.amount,
          type: e.type || 'Expense',
          description: e.description || ''
      }))
  .filter(e => {
      const matchesYear = e.date.getFullYear().toString() === selectedYear;
      const matchesProperty = selectedPropertyId === 'All' || String(e.propertyId) === String(selectedPropertyId);
      const matchesType = txTypeFilter === 'all' || 
          (txTypeFilter === 'income' && e.type === 'Income') || 
          (txTypeFilter === 'expense' && e.type === 'Expense');
      return matchesYear && matchesProperty && matchesType;
  })
  .sort((a,b) => b.date.getTime() - a.date.getTime());

  const totalIncomesFiltered = allTransactionsData.filter(t => t.type === 'Income').reduce((sum, t) => sum + Number(t.amount), 0);
  const totalExpensesFiltered = allTransactionsData.filter(t => t.type === 'Expense').reduce((sum, t) => sum + Number(t.amount), 0);
  const netBalanceFiltered = totalIncomesFiltered - totalExpensesFiltered;

  let printSummaryParts = [];
  if (txTypeFilter === 'all' || txTypeFilter === 'income') {
      printSummaryParts.push(`إجمالي الإيرادات: ${totalIncomesFiltered.toLocaleString()} د.ك`);
  }
  if (txTypeFilter === 'all' || txTypeFilter === 'expense') {
      printSummaryParts.push(`إجمالي المصروفات: ${totalExpensesFiltered.toLocaleString()} د.ك`);
  }
  if (txTypeFilter === 'all') {
      printSummaryParts.push(`صافي التدفق (الربح): ${netBalanceFiltered.toLocaleString()} د.ك`);
  }
  const printSummaryHtml = printSummaryParts.join(' | ');

  const txData = allTransactionsData.map(e => ({
      ...e,
      dateString: e.date.toLocaleDateString('ar-KW')
  }));

  const txColumns: Column[] = [
      { header: 'التاريخ', accessor: 'dateString' },
      { header: 'العقار', accessor: 'propertyName' },
      { header: 'النوع', accessor: 'type', render: val => val === 'Income' ? <span className="text-emerald-600 bg-emerald-50 px-2 rounded">إيراد</span> : <span className="text-rose-600 bg-rose-50 px-2 rounded">مصروف</span> },
      { header: 'التصنيف', accessor: 'category' },
      { header: 'البيان والوصف', accessor: 'description' },
      { header: 'المبلغ', accessor: 'amount', render: (val, row) => <span className={`font-bold ${row.type === 'Income' ? 'text-emerald-600' : 'text-rose-600'}`}>{row.type === 'Income' ? '+' : '-'}{Number(val).toLocaleString()} د.ك</span> }
  ];

  const customFilters = (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {['monthly', 'pnl', 'unit_yield', 'ledger', 'expenses'].includes(selectedReport) && (
            <select 
              className="border-slate-200 rounded-xl p-2 bg-white text-xs font-bold shadow-sm"
              value={selectedYear} 
              onChange={(e) => setSelectedYear(e.target.value)}
            >
                {years.length > 0 ? years.map(y => <option key={y} value={y}>{y}</option>) : <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>}
            </select>
        )}

        {(selectedReport === 'unit_yield' || selectedReport === 'expenses') && (
            <select 
            className="border-slate-200 rounded-xl p-2 bg-white text-xs font-bold shadow-sm max-w-[150px]"
            value={selectedPropertyId} 
            onChange={(e) => setSelectedPropertyId(e.target.value)}
            >
                <option value="All">جميع العقارات</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
        )}

        {selectedReport === 'expenses' && (
            <select 
              className="border-slate-200 rounded-xl p-2 bg-white text-xs font-bold shadow-sm"
              value={txTypeFilter} 
              onChange={(e) => setTxTypeFilter(e.target.value as 'all' | 'income' | 'expense')}
            >
                <option value="all">الكل (إيرادات ومصروفات)</option>
                <option value="income">إيرادات فقط</option>
                <option value="expense">مصروفات فقط</option>
            </select>
        )}
    </div>
  );

  return (
    <div className="space-y-6 slide-in max-w-full">
      <div className="flex flex-wrap gap-2 pl-4">
        {[
           { id: 'monthly', 'title': 'العوائد الشهرية (نقدي)' },
           { id: 'aging', 'title': 'أعمار الديون المتأخرة' },
           { id: 'pnl', 'title': 'بيان الأرباح والخسائر (استحقاق)' },
           { id: 'unit_yield', 'title': 'عوائد الوحدات' },
           { id: 'ledger', 'title': 'حركات الخزينة والبنك (فعلي)' },
           { id: 'expenses', 'title': 'سجل الإيرادات والمصروفات التفصيلي' }
        ].map(cat => (
          <button 
            key={cat.id}
            onClick={() => setSelectedReport(cat.id)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
              selectedReport === cat.id 
              ? 'bg-emerald-600 text-white shadow-lg' 
              : 'bg-white text-slate-500 hover:bg-emerald-50 border border-slate-200'
            }`}
          >
              {cat.title}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6 sm:p-10 shadow-lg min-h-[500px]">
        {selectedReport === 'monthly' && (
           <ReportTable 
             title="تقرير العوائد الشهرية للإيجارات" 
             description="يعرض هذا التقرير الإيجارات المحصلة فعلياً من المستأجرين شهرياً (الأساس النقدي - Cash Basis)"
             columns={monthlyColumns} 
             data={monthlyData} 
             customFilters={customFilters} 
           />
        )}
        {selectedReport === 'aging' && (
           <ReportTable 
             title="أعمار الديون والفواتير المتأخرة" 
             description="تحليل الفواتير المستحقة والمعلقة مقسمة بحسب فترات التأخير لمتابعة الديون والتحصيل (أعمار الديون)"
             columns={agingColumns} 
             data={agingData} 
           />
        )}
        {selectedReport === 'pnl' && (
           <ReportTable 
             title="البيان الختامي للربح والخسارة" 
             description="البيان الختامي لقياس الأداء المالي والأرباح والخسائر الشاملة بناءً على الفواتير الصادرة والمصروفات المستحقة (أساس الاستحقاق المحاسبي - Accrual Basis)"
             columns={pnlColumns} 
             data={pnlData} 
             customFilters={customFilters} 
           />
        )}
        {selectedReport === 'unit_yield' && (
           <ReportTable 
             title="صافي أرباح وعوائد وحدات العقار" 
             description="تحليل عوائد الأرباح والربحية الصافية لكل وحدة عقارية على حدة بمقارنة الإيرادات المحصلة بحصتها من المصروفات"
             columns={unitsYieldColumns} 
             data={unitsYieldData} 
             customFilters={customFilters} 
           />
        )}
        {selectedReport === 'ledger' && (
           <ReportTable 
             title="دفتر اليومية وحركات الخزينة المجمعة" 
             description="سجل تفصيلي لكافة الحركات النقدية وحركة المقبوضات والمدفوعات الفعلية في الصندوق والبنك (الأساس النقدي - Cash Basis)"
             columns={ledgerColumns} 
             data={ledgerData} 
             customFilters={customFilters} 
           />
        )}
        {selectedReport === 'expenses' && (
           <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                 {(txTypeFilter === 'all' || txTypeFilter === 'income') && (
                    <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-4 flex flex-col justify-center shadow-sm">
                       <span className="text-[10px] text-slate-500 font-black">إجمالي الإيرادات</span>
                       <span className="text-lg font-black text-emerald-600 mt-1">{totalIncomesFiltered.toLocaleString()} د.ك</span>
                    </div>
                 )}
                 {(txTypeFilter === 'all' || txTypeFilter === 'expense') && (
                    <div className="bg-rose-50/50 border border-rose-100 rounded-2xl p-4 flex flex-col justify-center shadow-sm">
                       <span className="text-[10px] text-slate-500 font-black">إجمالي المصروفات</span>
                       <span className="text-lg font-black text-rose-600 mt-1">{totalExpensesFiltered.toLocaleString()} د.ك</span>
                    </div>
                 )}
                 {txTypeFilter === 'all' && (
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 flex flex-col justify-center shadow-sm">
                       <span className="text-[10px] text-slate-500 font-black">صافي التدفق (الربح)</span>
                       <span className={`text-lg font-black mt-1 ${netBalanceFiltered >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {netBalanceFiltered >= 0 ? '+' : ''}{netBalanceFiltered.toLocaleString()} د.ك
                       </span>
                    </div>
                 )}
              </div>
              <ReportTable 
                title="سجل الإيرادات والمصروفات التفصيلي" 
                description="تقرير تفصيلي بكافة المعاملات المالية (إيرادات ومصاريف) التي تمت على المحافظ والعقارات"
                columns={txColumns} 
                data={txData} 
                customFilters={customFilters} 
                printSummaryHtml={printSummaryHtml}
              />
           </div>
        )}
      </div>
    </div>
  );
};

export default FinancialReports;
